(function () {
  function parseJson(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.error('Variant bundle builder: JSON parse failed', e);
      return null;
    }
  }

  function formatMoney(cents) {
    if (window.Shopify?.formatMoney) {
      return window.Shopify.formatMoney(cents);
    }
    return (cents / 100).toFixed(2).replace('.', ',') + '\u00a0€';
  }

  class VariantBundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;

      this.formId = this.getAttribute('data-form-id');
      this.drawerId = this.getAttribute('data-drawer-id');
      this.unitPrice = parseInt(this.getAttribute('data-unit-price'), 10) || 0;
      this.unitComparePrice = parseInt(this.getAttribute('data-unit-compare-price'), 10) || this.unitPrice;
      if (this.unitComparePrice < this.unitPrice) {
        this.unitComparePrice = this.unitPrice;
      }
      this.discountMode = this.getAttribute('data-discount-mode') || 'fixed';
      this.discountQty2 = parseInt(this.getAttribute('data-discount-qty-2'), 10) || 0;
      this.discountQty3 = parseInt(this.getAttribute('data-discount-qty-3'), 10) || 0;
      this.discountQty4 = parseInt(this.getAttribute('data-discount-qty-4'), 10) || 0;
      this.presetCount = parseInt(this.getAttribute('data-preset-count'), 10) || 3;
      this.maxQuantity = parseInt(this.getAttribute('data-max-quantity'), 10) || this.presetCount;
      this.productId = this.getAttribute('data-product-id') || '';
      this.giftVariantId = parseInt(this.getAttribute('data-gift-variant-id'), 10) || 0;
      this.giftThreshold = parseInt(this.getAttribute('data-gift-threshold'), 10) || 0;
      this.enableOptional = this.getAttribute('data-enable-optional') === 'true';
      this.hideVariantSlots = this.getAttribute('data-hide-variant-slots') === 'true';
      this.optionalLabel =
        this.getAttribute('data-optional-label') || 'Optional auswählen...';
      this.placeholderText = this.getAttribute('data-placeholder-text') || 'Farbe auswählen...';
      this.redirectToCheckout = this.getAttribute('data-redirect-to-checkout') === 'true';
      this.shopDisplayDiscount = parseInt(this.getAttribute('data-shop-display-discount'), 10) || 0;

      this.variants = parseJson(this.querySelector('[data-vbb-variants]')) || [];
      this.presets = parseJson(this.querySelector('[data-vbb-presets]')) || [];
      this.presetGalleryImages = parseJson(this.querySelector('[data-vbb-preset-gallery]')) || {};

      this.slotsEl = this.querySelector('[data-vbb-slots]');
      this.slotTemplate = this.querySelector('[data-vbb-slot-template]');
      this.qtyTemplate = this.querySelector('[data-vbb-qty-template]');
      this.drawer = document.getElementById(this.drawerId);
      this.drawerList = this.querySelector('[data-vbb-drawer-list]');
      this.drawerConfirm = this.querySelector('[data-vbb-drawer-confirm]');
      this.drawerConfirmCount = this.querySelector('[data-vbb-drawer-confirm-count]');
      this.drawerTotal = this.querySelector('[data-vbb-drawer-total]');
      this.drawerCompare = this.querySelector('[data-vbb-drawer-compare]');
      this.atcButton = this.querySelector('[data-vbb-atc]');

      this.baseQuantity = this.presets[0]?.quantity || 1;
      this.selections = [];
      this.drawerQuantities = {};
      this.drawerTargetQty = this.baseQuantity;

      this._bind();
      this._bindGallery();
      this._selectPresetByQuantity(this.baseQuantity);
      if (this.hideVariantSlots) {
        this._autofillSingleVariant();
      } else {
        this._renderSlots();
        this._renderDrawer();
      }
      this._updateAtcState();
    }

    _bind() {
      this.querySelectorAll('[data-vbb-preset]').forEach((button) => {
        button.addEventListener('click', () => {
          const qty = parseInt(button.getAttribute('data-quantity'), 10) || 1;
          this._selectPresetByQuantity(qty);
          if (this.hideVariantSlots) {
            this._autofillSingleVariant();
          } else {
            this._trimSelections();
            this._renderSlots();
          }
          this._updateAtcState();
        });
      });

      this.slotsEl?.addEventListener('click', (event) => {
        const openBtn = event.target.closest('[data-vbb-open-drawer]');
        if (openBtn) {
          const wrap = openBtn.closest('[data-vbb-slot-wrap]');
          const isOptional = wrap?.getAttribute('data-optional') === 'true';
          this._openDrawer(isOptional);
          return;
        }
        const removeBtn = event.target.closest('[data-slot-remove]');
        if (removeBtn) {
          const wrap = removeBtn.closest('[data-vbb-slot-wrap]');
          const index = parseInt(wrap?.getAttribute('data-slot-index'), 10);
          if (!Number.isNaN(index)) {
            this.selections.splice(index, 1);
            this._syncPresetFromSelections();
            this._renderSlots();
            this._updateAtcState();
          }
        }
      });

      this.drawerConfirm?.addEventListener('click', () => {
        this._applyDrawerToSelections();
        this._closeDrawer();
        this._syncPresetFromSelections();
        this._renderSlots();
        this._updateAtcState();
      });

      this.atcButton?.addEventListener('click', () => this._addToCart());
    }

    _singleVariant() {
      return this.variants.find((entry) => entry.available) || this.variants[0] || null;
    }

    _autofillSingleVariant() {
      const variant = this._singleVariant();
      if (!variant) {
        this.selections = [];
        return;
      }

      this.selections = Array.from({ length: this.baseQuantity }, () => ({
        id: Number(variant.id),
        title: variant.title,
        image: variant.image
      }));
    }

    _bindGallery() {
      const form = this.formId ? document.forms[this.formId] : null;
      form?.addEventListener('variant:change', () => {
        requestAnimationFrame(() => this._syncGalleryPresetImage());
      });
    }

    _galleryRoot() {
      const form = this.formId ? document.forms[this.formId] : null;
      return (
        form?.closest('.shopify-section')?.querySelector('product-gallery') ||
        document.querySelector('product-gallery')
      );
    }

    _syncGalleryPresetImage() {
      const image = this.presetGalleryImages[String(this.baseQuantity)];
      if (!image?.src) return;

      const gallery = this._galleryRoot();
      if (!gallery) return;

      const slide = gallery.querySelector('[data-vbb-preset-media]');
      const img = slide?.querySelector('img');
      if (img) {
        img.src = image.src;
        if (image.srcset) img.srcset = image.srcset;
        if (image.width) img.setAttribute('width', String(image.width));
        if (image.height) img.setAttribute('height', String(image.height));
        img.alt = image.alt || '';
      }

      const thumb = gallery.querySelector('[data-vbb-preset-thumb] img');
      if (thumb) {
        thumb.src = image.thumb || image.src;
        thumb.alt = image.alt || '';
      }

      const carousel = gallery.querySelector('media-carousel');
      if (carousel && typeof carousel.select === 'function') {
        carousel.select(0, { animate: false, force: true });
      }

      gallery.querySelectorAll('page-dots').forEach((dots) => {
        if (typeof dots.select === 'function') dots.select(0, false);
      });
    }

    _selectPresetByQuantity(qty) {
      this.baseQuantity = Math.min(Math.max(qty, 1), this.maxQuantity);
      this._syncPresetHighlight();
      this._syncGalleryPresetImage();
    }

    _syncPresetHighlight() {
      const highlightQty =
        this.selections.length > this.baseQuantity ? this.selections.length : this.baseQuantity;
      this.querySelectorAll('[data-vbb-preset]').forEach((button) => {
        const qty = parseInt(button.getAttribute('data-quantity'), 10);
        const selected = qty === highlightQty;
        button.classList.toggle('is-selected', selected);
        if (selected) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });
    }

    _effectiveQuantity() {
      return Math.max(this.baseQuantity, this.selections.length);
    }

    _requiredFilled() {
      return this.selections.length >= this.baseQuantity;
    }

    _shouldShowOptional() {
      return (
        this.enableOptional &&
        this._requiredFilled() &&
        this.selections.length === this.baseQuantity &&
        this.baseQuantity < this.maxQuantity
      );
    }

    _slotCount() {
      return this.baseQuantity + (this._shouldShowOptional() ? 1 : 0);
    }

    _trimSelections() {
      if (this.selections.length > this.baseQuantity) {
        this.selections = this.selections.slice(0, this.baseQuantity);
      }
    }

    _syncPresetFromSelections() {
      const match = this.presets.find((entry) => entry.quantity === this.selections.length);
      if (match) {
        this.baseQuantity = match.quantity;
      }
      this._syncPresetHighlight();
      this._syncGalleryPresetImage();
    }

    _tierValueForQuantity(qty) {
      if (qty >= 4) return this.discountQty4 || 0;
      if (qty >= 3) return this.discountQty3 || 0;
      if (qty >= 2) return this.discountQty2 || 0;
      return 0;
    }

    _discountForQuantity(qty, subtotalCents = null) {
      const tier = this._tierValueForQuantity(qty);
      if (!tier) return 0;

      if (this.discountMode === 'percent') {
        const base = subtotalCents != null && qty > 0 ? Math.round(subtotalCents / qty) : this.unitPrice;
        const unitDiscount = Math.floor((base * tier) / 100);
        return unitDiscount * qty;
      }

      return tier;
    }

    _variantCompareUnit(variant) {
      if (!variant) return this.unitComparePrice;
      const compareAt = parseInt(variant.compare_at_price, 10) || 0;
      const price = parseInt(variant.price, 10) || 0;
      return compareAt > price ? compareAt : price;
    }

    _sumSelectionPricing(quantities) {
      let subtotalCents = 0;
      let compareCents = 0;

      Object.entries(quantities).forEach(([id, qty]) => {
        const count = qty || 0;
        if (count <= 0) return;
        const variant = this.variants.find((entry) => String(entry.id) === String(id));
        if (!variant) return;
        subtotalCents += (parseInt(variant.price, 10) || 0) * count;
        compareCents += this._variantCompareUnit(variant) * count;
      });

      return { subtotalCents, compareCents };
    }

    _getPricing(quantity, quantities = null) {
      const qty = quantity || this._effectiveQuantity();
      let subtotalCents = this.unitPrice * qty;
      let compareCents = this.unitComparePrice * qty;

      if (quantities && Object.keys(quantities).length > 0) {
        const summed = this._sumSelectionPricing(quantities);
        const filledQty = Object.values(quantities).reduce((sum, count) => sum + (count || 0), 0);
        const remainingQty = Math.max(0, qty - filledQty);
        subtotalCents = summed.subtotalCents + this.unitPrice * remainingQty;
        compareCents = summed.compareCents + this.unitComparePrice * remainingQty;
      }

      let discountCents = this._discountForQuantity(qty, subtotalCents);
      let finalCents = Math.max(0, subtotalCents - discountCents);
      let savingsCents = Math.max(0, compareCents - finalCents);

      if (this.shopDisplayDiscount > 0 && subtotalCents > 0) {
        const listFinal = subtotalCents;
        finalCents = Math.round((listFinal * (100 - this.shopDisplayDiscount)) / 100);
        compareCents = Math.max(compareCents, listFinal);
        discountCents = Math.max(0, listFinal - finalCents);
        savingsCents = Math.max(0, compareCents - finalCents);
      }

      return { finalCents, compareCents, discountCents, savingsCents, subtotalCents };
    }

    _renderSlots() {
      if (!this.slotsEl || !this.slotTemplate) return;
      this.slotsEl.innerHTML = '';
      const slotCount = this._slotCount();

      for (let i = 0; i < slotCount; i += 1) {
        const isOptional = i >= this.baseQuantity;
        const node = this.slotTemplate.content.firstElementChild.cloneNode(true);
        node.setAttribute('data-slot-index', String(i));
        if (isOptional) node.setAttribute('data-optional', 'true');

        const selection = this.selections[i];
        const imageEl = node.querySelector('[data-slot-image]');
        const labelEl = node.querySelector('[data-slot-label]');
        const checkEl = node.querySelector('[data-slot-check]');
        const removeEl = node.querySelector('[data-slot-remove]');
        const button = node.querySelector('[data-vbb-open-drawer]');
        const emptyLabel = isOptional ? this.optionalLabel : this.placeholderText;

        if (selection) {
          node.classList.add('is-filled');
          button?.classList.add('is-filled');
          if (labelEl) labelEl.textContent = selection.title || emptyLabel;
          if (checkEl) checkEl.hidden = false;
          if (removeEl) removeEl.hidden = false;
          if (selection.image && imageEl) {
            const img = document.createElement('img');
            img.src = selection.image;
            img.alt = selection.title || '';
            img.width = 56;
            img.height = 56;
            img.loading = 'eager';
            img.decoding = 'async';
            imageEl.replaceChildren(img);
          }
        } else {
          if (labelEl) labelEl.textContent = emptyLabel;
          if (checkEl) checkEl.hidden = true;
          if (removeEl) removeEl.hidden = true;
        }

        this.slotsEl.appendChild(node);
      }
    }

    _openDrawer(forOptional = false) {
      this.drawerTargetQty = forOptional
        ? Math.min(this.baseQuantity + 1, this.maxQuantity)
        : this._slotCount();
      this.drawerQuantities = {};
      this.selections.forEach((sel) => {
        if (!sel?.id) return;
        const key = String(sel.id);
        this.drawerQuantities[key] = (this.drawerQuantities[key] || 0) + 1;
      });
      if (!this.drawerList?.children.length) {
        this._renderDrawer();
      }
      this._syncDrawerQtyDisplay();
      this._updateDrawerFooter();
      this.drawer?.show?.();
    }

    _closeDrawer() {
      this.drawer?.hide?.();
    }

    _sortedVariants() {
      return [...this.variants].sort((a, b) => {
        if (Boolean(a.available) === Boolean(b.available)) return 0;
        return a.available ? -1 : 1;
      });
    }

    _renderDrawer() {
      if (!this.drawerList || !this.qtyTemplate) return;
      this.drawerList.innerHTML = '';
      const fragment = document.createDocumentFragment();

      this._sortedVariants().forEach((variant, index) => {
        const row = document.createElement('div');
        row.className = 'bk-media-bundle-builder__drawer-item';
        row.setAttribute('data-drawer-item', '');
        row.setAttribute('data-variant-id', String(variant.id));
        row.setAttribute('data-available', variant.available ? 'true' : 'false');
        if (!variant.available) row.classList.add('is-unavailable');

        const imageWrap = document.createElement('div');
        imageWrap.className = 'bk-media-bundle-builder__drawer-item-image';
        if (variant.image) {
          const img = document.createElement('img');
          img.src = variant.image;
          img.alt = variant.title || '';
          img.width = 56;
          img.height = 56;
          img.decoding = 'async';
          img.loading = index < 6 ? 'eager' : 'lazy';
          if (index < 3) img.fetchPriority = 'high';
          imageWrap.appendChild(img);
        }

        const info = document.createElement('div');
        info.className = 'bk-media-bundle-builder__drawer-item-info v-stack gap-1';
        const title = document.createElement('span');
        title.className = 'text-base bk-media-bundle-builder__drawer-item-title';
        title.textContent = variant.title || '';
        info.appendChild(title);

        if (!variant.available) {
          const soldOut = document.createElement('span');
          soldOut.className = 'text-xs bk-media-bundle-builder__colors-variant-sold-out';
          soldOut.textContent = 'Ausverkauft';
          info.appendChild(soldOut);
        }

        const qtyNode = this.qtyTemplate.content.firstElementChild.cloneNode(true);
        const valueInput = qtyNode.querySelector('[data-qty-value]');
        if (valueInput) valueInput.value = '0';

        qtyNode.querySelector('[data-qty-minus]')?.addEventListener('click', () => {
          const live = this.drawerQuantities[String(variant.id)] || 0;
          this._setDrawerQty(variant.id, live - 1);
        });
        qtyNode.querySelector('[data-qty-plus]')?.addEventListener('click', () => {
          const live = this.drawerQuantities[String(variant.id)] || 0;
          this._setDrawerQty(variant.id, live + 1);
        });
        valueInput?.addEventListener('keydown', (event) => event.preventDefault());

        if (!variant.available) {
          qtyNode.querySelectorAll('button').forEach((btn) => {
            btn.disabled = true;
          });
        }

        row.appendChild(imageWrap);
        row.appendChild(info);
        row.appendChild(qtyNode);
        fragment.appendChild(row);
      });

      this.drawerList.appendChild(fragment);
      this._syncDrawerQtyDisplay();
    }

    _drawerTotalQty() {
      return Object.values(this.drawerQuantities).reduce((sum, qty) => sum + (qty || 0), 0);
    }

    _setDrawerQty(variantId, nextQty) {
      const key = String(variantId);
      const currentTotal = this._drawerTotalQty();
      const current = this.drawerQuantities[key] || 0;
      let qty = Math.max(0, nextQty || 0);
      const target = this.drawerTargetQty || this.baseQuantity;

      if (qty > current) {
        const room = target - (currentTotal - current);
        qty = Math.min(qty, current + Math.max(room, 0));
      }

      if (qty <= 0) delete this.drawerQuantities[key];
      else this.drawerQuantities[key] = qty;

      this._syncDrawerQtyDisplay();
      this._updateDrawerFooter();
    }

    _syncDrawerQtyDisplay() {
      if (!this.drawerList) return;
      const total = this._drawerTotalQty();
      const target = this.drawerTargetQty || this.baseQuantity;

      this.drawerList.querySelectorAll('[data-drawer-item]').forEach((item) => {
        const id = item.getAttribute('data-variant-id');
        const val = this.drawerQuantities[id] || 0;
        const isUnavailable = item.getAttribute('data-available') === 'false';
        const input = item.querySelector('[data-qty-value]');
        const minusBtn = item.querySelector('[data-qty-minus]');
        const plusBtn = item.querySelector('[data-qty-plus]');

        if (input) {
          input.value = String(val);
          input.size = Math.max(String(val).length + 1, 2);
        }
        if (minusBtn) minusBtn.disabled = val <= 0 || isUnavailable;
        if (plusBtn) plusBtn.disabled = isUnavailable || total >= target;
        item.classList.toggle('is-selected', val > 0);
      });
    }

    _updateDrawerFooter() {
      const total = this._drawerTotalQty();
      const target = this.drawerTargetQty || this.baseQuantity;
      const pricing = this._getPricing(Math.max(target, this.baseQuantity), this.drawerQuantities);

      if (this.drawerConfirmCount) {
        this.drawerConfirmCount.textContent = `${total}/${target} ausgewählt`;
      }
      if (this.drawerConfirm) {
        this.drawerConfirm.disabled = total < this.baseQuantity || total > target;
      }
      if (this.drawerTotal) {
        this.drawerTotal.textContent = formatMoney(pricing.finalCents);
      }
      if (this.drawerCompare) {
        const showCompare = pricing.compareCents > pricing.finalCents;
        this.drawerCompare.hidden = !showCompare;
        if (showCompare) this.drawerCompare.textContent = formatMoney(pricing.compareCents);
      }
    }

    _applyDrawerToSelections() {
      const next = [];
      Object.entries(this.drawerQuantities).forEach(([id, qty]) => {
        const variant = this.variants.find((entry) => String(entry.id) === String(id));
        if (!variant || !qty) return;
        for (let i = 0; i < qty; i += 1) {
          next.push({
            id: Number(variant.id),
            title: variant.title,
            image: variant.image
          });
        }
      });
      const maxSlots = Math.min(this.drawerTargetQty || this.baseQuantity, this.maxQuantity);
      this.selections = next.slice(0, maxSlots);
    }

    _isComplete() {
      return this.selections.length >= this.baseQuantity && this.baseQuantity > 0;
    }

    _updateAtcState() {
      if (!this.atcButton) return;
      this.atcButton.disabled = !this._isComplete();
    }

    async _addToCart() {
      if (this.hideVariantSlots && !this._isComplete()) {
        this._autofillSingleVariant();
      }

      if (!this._isComplete()) {
        if (!this.hideVariantSlots) this._openDrawer(false);
        return;
      }

      const grouped = {};
      this.selections.forEach((sel) => {
        grouped[sel.id] = (grouped[sel.id] || 0) + 1;
      });

      const items = Object.entries(grouped).map(([id, quantity]) => ({
        id: parseInt(id, 10),
        quantity
      }));

      const selectedQty = this.selections.length;
      if (this.giftVariantId && this.giftThreshold > 0 && selectedQty >= this.giftThreshold) {
        const giftProperties = { _BundleGift: '1' };
        if (this.productId) {
          giftProperties._BundleGiftParentProductId = String(this.productId);
        }
        giftProperties._BundleGiftThreshold = String(this.giftThreshold);
        items.push({ id: this.giftVariantId, quantity: 1, properties: giftProperties });
      }

      const cartDrawerElement = document.querySelector('cart-drawer');
      const sectionId = cartDrawerElement?.getAttribute('data-section-id') || 'cart-drawer';
      let sectionsToBundle = ['variant-added'];
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );
      if (!sectionsToBundle.includes(sectionId)) {
        sectionsToBundle.push(sectionId);
      }

      try {
        this.atcButton?.setAttribute('aria-busy', 'true');
        this.atcButton.disabled = true;

        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections: sectionsToBundle.join(',') })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.description || 'Failed to add items to cart');
        }

        const responseJson = await response.json();
        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        if (!cartContent.sections) cartContent.sections = {};
        Object.assign(cartContent.sections, responseJson.sections || {});
        if (window.BkMediaBundleCartPricing?.attachFreshDrawerSection) {
          await window.BkMediaBundleCartPricing.attachFreshDrawerSection(cartContent);
        }

        const productForm = document.querySelector(`#${this.formId}`) || document.querySelector('form[is="product-form"]');
        const formToUse = productForm || document.createElement('form');
        let formAdded = false;
        if (!productForm) {
          document.body.appendChild(formToUse);
          formAdded = true;
        }

        formToUse.dispatchEvent(
          new CustomEvent('variant:add', {
            bubbles: true,
            cancelable: true,
            detail: {
              items: responseJson.items || items,
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          })
        );

        if (formAdded) document.body.removeChild(formToUse);

        if (this.redirectToCheckout) {
          window.location.href = window.Shopify.routes.root + 'checkout';
          return;
        }

        this.selections = [];
        if (this.hideVariantSlots) {
          this._autofillSingleVariant();
        } else {
          this._renderSlots();
        }
        this._updateAtcState();
      } catch (error) {
        console.error('Variant bundle builder ATC failed:', error);
        alert(error.message || 'Fehler beim Hinzufügen');
      } finally {
        this.atcButton?.removeAttribute('aria-busy');
        this._updateAtcState();
      }
    }
  }

  if (!customElements.get('variant-bundle-builder')) {
    customElements.define('variant-bundle-builder', VariantBundleBuilder);
  }

  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('variant-bundle-builder').forEach((el) => {
      if (typeof el.connectedCallback === 'function') el.connectedCallback();
    });
  });
})();
